import { useEffect, useContext, useReducer } from "react";

import camelcase from "camel-case";

import R from "ramda";

import { AuthenticationContext } from "src/banking/AuthenticationContext";
import sdk from "src/sdk";

const handleCurrentState = async (values, { loginState, dispatch }) => {
  switch (loginState.state) {
    case "entry":
      const loginResponse = await sdk.submitLogin(values);

      dispatch({
        type: "entryComplete",
        state: camelcase(loginResponse.data.state),
        links: loginResponse.links,
        availableChannels: loginResponse.data.availableChannels,
        disclosureUrl: loginResponse.data.disclosureUrl,
      });

      break;

    case "disclosureAccept":
      const acceptUrl = R.path(["links", "disclosureAccept"], loginState);

      const acceptResponse = await sdk.acceptDisclosure(acceptUrl);

      dispatch({
        ...acceptResponse.data,
        type: "acceptComplete",
        state: camelcase(acceptResponse.data.state),
        links: acceptResponse.links,
      });

      break;

    case "challengeOutofband":
      const challengeResponse = await sdk.submitOutOfBand(values, {
        path: loginState.links.tierChallenge || loginState.links.registerDevice,
      });

      const newOutOfBandState = R.pathOr(
        "loginComplete",
        ["data", "state"],
        challengeResponse
      );

      dispatch({
        type: "outOfBandComplete",
        state: camelcase(newOutOfBandState),
        links: challengeResponse.links,
        disclosureUrl: R.path(["data", "disclosureUrl"], challengeResponse),
      });

      break;

    case "challengeQuestion":
      const challengeQuestionResponse = await sdk.submitChallengeAnswer(
        {
          challengeId: loginState.challengeId,
          challengeAnswer: values.answer,
        },
        {
          path: loginState.links.challengeQuestion,
        }
      );

      const newState = R.pathOr(
        "loginComplete",
        ["data", "state"],
        challengeQuestionResponse
      );

      dispatch({
        type: "challengeQuestionComplete",
        state: camelcase(newState),
        links: challengeQuestionResponse.links,
        disclosureUrl: R.path(
          ["data", "disclosureUrl"],
          challengeQuestionResponse
        ),
      });

      break;

    default:
      return;
  }
};

const reducer = (loginState, action) => {
  switch (action.type) {
    case "entryComplete":
    case "acceptComplete":
      return {
        ...loginState,
        state: action.state,
        links: action.links,
        availableChannels: action.availableChannels,
        disclosureUrl: action.disclosureUrl,
      };
    case "outOfBandComplete":
      return {
        ...loginState,
        state: action.state,
        links: action.links,
        availableChannels: null,
        disclosureUrl: action.disclosureUrl,
      };
    case "challengeQuestionComplete":
      return {
        ...loginState,
        state: action.state,
        links: action.links,
        availableChannels: null,
        disclosureUrl: action.disclosureUrl,
      };
    case "selectChannel":
      return {
        ...loginState,
        sending: true,
      };
    case "selectChannelComplete":
      return {
        ...loginState,
        sending: false,
        sent: true,
        links: {
          ...loginState.links,
          ...action.links,
        },
      };
    case "requestNewCode":
      return {
        ...loginState,
        sending: false,
        sent: false,
      };
    default:
      return loginState;
  }
};

export const useLogin = ({ onComplete, initialState }) => {
  const authenticationContext = useContext(AuthenticationContext);

  const [loginState, dispatch] = useReducer(reducer, {
    sending: false,
    sent: false,
    state: "entry",
    ...initialState,
  });

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      await handleCurrentState(values, {
        loginState,
        dispatch,
      });
    } catch (e) {
      setSubmitting(false);
      throw e;
    }
  };

  useEffect(() => {
    if (loginState.state === "loginComplete") {
      authenticationContext.setLoggedIn(true);
      authenticationContext.setLoginVisible(false);
      if (onComplete) {
        authenticationContext.onLoginComplete();
        onComplete();
      }
    }
  }, [loginState.state]);

  const onSelectChannel = async channel => {
    dispatch({
      type: "selectChannel",
    });

    const result = await sdk.triggerOutOfBand(channel, {
      path: loginState.links.challengeOutofband,
    });

    dispatch({
      type: "selectChannelComplete",
      links: result.links,
    });
  };

  const requestNewCode = () => {
    dispatch({
      type: "requestNewCode",
    });
  };

  const outOfBand = {
    sent: loginState.sent,
    sending: loginState.sending,
    onSelectChannel,
    requestNewCode,
  };

  return {
    onSubmit,
    loginState,
    maintenanceStatusErrors: loginState.maintenanceErrors,
    outOfBand,
  };
};



// WEBPACK FOOTER //
// ./src/banking/authentication/Login/hooks.js